import { useMemo } from 'react';

import type { RewardPool, RewardPoolTicketCurrency } from '@shared/api/types/rewardPools';
import { useRewardPoolSelfEntries } from '@shared/hooks/query/rewardPools';

import type { DialogProps } from '@radix-ui/react-dialog';
import { CustomDialog, DialogCancelButton } from '@ui/uikit/components/common/Dialog';

import { TicketsList } from './TicketsList';
import type { TicketListItemType } from './TicketsListItem';
import { TicketsListSkeleton } from './TicketsListSkeleton';

type Props = DialogProps & {
  rewardPool: RewardPool;
  closeModal: () => void;
  currency?: RewardPoolTicketCurrency;
};

export const ViewTicketsModal = ({ rewardPool, currency, ...props }: Props) => {
  const { data: entries = [], isLoading } = useRewardPoolSelfEntries(rewardPool.id);

  const filteredByCurrencyEntries: TicketListItemType[] = useMemo(() => {
    const filteredEntries = currency
      ? entries.filter((entry) => entry.currency === currency)
      : entries;

    return filteredEntries.map((entry) => {
      return {
        isWinner: Boolean(entry.reward),
        code: entry.id,
      };
    });
  }, [entries, currency]);

  return (
    <CustomDialog
      {...props}
      title={`My Tickets in ${rewardPool.name}`}
      titleIsLeftAligned
      noSidePadding
      size="md"
      footer={<DialogCancelButton className="!w-[220px] max-sm">Close</DialogCancelButton>}
    >
      {isLoading ? (
        <TicketsListSkeleton />
      ) : (
        <div className="h-[500px] max-h-[55vh]">
          <TicketsList
            className="py-6 px-4"
            tickets={filteredByCurrencyEntries}
            isLoading={isLoading}
          />
        </div>
      )}
    </CustomDialog>
  );
};
